import { bucketContaining, type AgentTaskBucket, type BucketableTask } from './agent-task-buckets';

/**
 * WHICH tasks offer "Cancel this task" — the one rule, shared by web, iOS and Android.
 *
 * A task can be called off exactly while it is still OPEN: the user's turn (`waiting`) or the agent's
 * (`inProgress`). Everything terminal — `done` (COMPLETED / CANCELLED) and `failed` — is already over,
 * so offering to cancel it would be a control that does nothing.
 *
 * ⚠️ Expressed over the BUCKET, never over a hand-written status list. `bucketContaining` already folds
 * PENDING / IN_PROGRESS / WAITING / PAUSED / IN_REVIEW / anything-unknown into the two open buckets, and
 * that folding is the part that must not be duplicated: a hand-rolled list forgets IN_REVIEW the day the
 * backend starts emitting it, and the affordance silently disappears from the tasks that need it most.
 * An unknown future status defaults to `inProgress`, so it stays cancellable — the safe direction, since
 * the server is the one that decides whether the cancel is legal.
 *
 * Mirrored 1:1 by `TaskCancel.swift` (iOS) and `TaskCancel.kt` (Android), tested against identical
 * fixtures on all three surfaces.
 */
export const CANCELLABLE_BUCKETS: AgentTaskBucket[] = ['waiting', 'inProgress'];

export function bucketIsCancellable(bucket: AgentTaskBucket): boolean {
  return CANCELLABLE_BUCKETS.includes(bucket);
}

export function statusIsCancellable(status: string | undefined): boolean {
  return bucketIsCancellable(bucketContaining(status));
}

export function taskIsCancellable(task: BucketableTask): boolean {
  return statusIsCancellable(task.status);
}
